import type { 
  AppError, 
  LegacyError
} from '../../types/errors';
import { 
  createValidationError,
  createAuthenticationError,
  createAuthorizationError,
  createResourceError,
  createConflictError,
  createBusinessError,
  createRateLimitError,
  createExternalServiceError,
  createSystemError,
  convertLegacyError,
  isSystemError,
  isExternalServiceError,
  isRateLimitError,
  isAuthorizationError,
  isBusinessError
} from '../../types/errors';

// Error handling utilities for the new hierarchical error structure

/**
 * Converts various error formats to AppError
 */
export function normalizeError(error: any): AppError {
  // IMPORTANT: Check for already-attached AppError FIRST (from API interceptor)
  if (error && typeof error === 'object' && 'appError' in error) {
    return error.appError as AppError;
  }

  // Handle backend format with both errorCode and subtype (primary format)
  if (error && typeof error === 'object' && 'errorCode' in error && 'subtype' in error) {
    // Use the subtype from backend to create the appropriate error
    const errorCode = error.errorCode;
    const subtype = error.subtype;
    const message = error.errorMessage || error.message || 'An error occurred';
    
    // Map errorCode to error type and use backend's subtype
    if (errorCode === 400) {
      return createValidationError(subtype, message, error);
    } else if (errorCode === 401) {
      return createAuthenticationError(subtype, message, error);
    } else if (errorCode === 402) {
      return createBusinessError(subtype, message, error);
    } else if (errorCode === 403) {
      return createAuthorizationError(subtype, message, error);
    } else if (errorCode === 404) {
      return createResourceError(subtype, message, error);
    } else if (errorCode === 409) {
      return createConflictError(subtype, message, error);
    } else if (errorCode === 424) {
      return createExternalServiceError(subtype, message, error);
    } else if (errorCode === 429) {
      return createRateLimitError(subtype, message, error);
    } else if (errorCode >= 500) {
      return createSystemError(subtype, message, error);
    }
  }

  // Handle legacy error format without subtype (fallback for old errors)
  if (error && typeof error === 'object' && 'errorCode' in error) {
    return convertLegacyError(error as LegacyError);
  }

  // Handle HTTP response errors
  if (error?.response?.status) {
    return handleHttpError(error);
  }

  // Handle network errors
  if (error?.name === 'NetworkError' || error?.code === 'NETWORK_ERROR') {
    return createExternalServiceError('stripe_service_down', 'Network connection failed. Please check your internet connection and try again.');
  }

  // Handle generic errors
  if (error instanceof Error) {
    return createSystemError('internal_processing_error', error.message);
  }

  // Fallback
  return createSystemError('internal_processing_error', 'An unexpected error occurred. Please try again.');
}

/**
 * Handles HTTP response errors and converts them to appropriate AppError types
 */
function handleHttpError(error: any): AppError {
  const status = error.response.status;
  const errorData = error.response.data;
  const message = errorData?.message || errorData?.errorMessage || 'An error occurred';

  switch (status) {
    case 400:
      return createValidationError('validation_missing', message, errorData);
    case 401:
      return createAuthenticationError('wrong_credentials', message, errorData);
    case 402:
      return createBusinessError('payment_failed', message, errorData);
    case 403:
      return createAuthorizationError('access_denied', message, errorData);
    case 404:
      return createResourceError('user_not_found', message, errorData);
    case 409:
      return createConflictError('email_already_registered', message, errorData);
    case 424:
      return createExternalServiceError('stripe_service_down', message, errorData);
    case 429:
      return createRateLimitError('too_many_requests', message, errorData);
    case 500:
    case 502:
    case 503:
    case 504:
      return createSystemError('internal_processing_error', message, errorData);
    default:
      return createSystemError('internal_processing_error', message, errorData);
  }
}

/**
 * Creates specific error types based on context
 */
export const ErrorFactory = {
  validation: {
    invalidEmail: (message?: string) => 
      createValidationError('invalid_email', message || 'Please enter a valid email address'),
    
    missingPassword: (message?: string) => 
      createValidationError('missing_password', message || 'Password is required'),
    
    validationMissing: (message?: string) => 
      createValidationError('validation_missing', message || 'Please fill in all required fields'),
    
    paymentExceeding: (message?: string) => 
      createValidationError('payment_exceeding', message || 'Payment amount exceeds your limit'),
    
    invalidFileType: (message?: string) => 
      createValidationError('invalid_file_type', message || 'This file type is not supported'),
    
    fileTooLarge: (message?: string) => 
      createValidationError('file_too_large', message || 'File size is too large. Please choose a smaller file'),
    
    invalidDateFormat: (message?: string) => 
      createValidationError('invalid_date_format', message || 'Please enter a valid date'),
  },

  authentication: {
    noApiKey: (message?: string) => 
      createAuthenticationError('no_api_key', message || 'API key is missing'),
    
    invalidToken: (message?: string) => 
      createAuthenticationError('invalid_token', message || 'Your session has expired. Please log in again'),
    
    tokenExpired: (message?: string) => 
      createAuthenticationError('token_expired', message || 'Your session has expired. Please log in again'),
    
    wrongCredentials: (message?: string) => 
      createAuthenticationError('wrong_credentials', message || 'Invalid email or password'),
    
    accountLocked: (message?: string) => 
      createAuthenticationError('account_locked', message || 'Account is temporarily locked'),

    exceedAttemptLimits: (message?: string) =>
      createValidationError('exceed_attempt_limits', message || 'Too many attempts. Please try again after 15 minutes'),
  },

  authorization: {
    insufficientPermissions: (message?: string) => 
      createAuthorizationError('insufficient_permissions', message || 'You don\'t have permission to access this feature'),
    
    accessDenied: (message?: string) => 
      createAuthorizationError('access_denied', message || 'Access denied. Please contact support'),
    
    accountSuspended: (message?: string) => 
      createAuthorizationError('account_suspended', message || 'Your account has been suspended'),
    
    featureRestricted: (message?: string) => 
      createAuthorizationError('feature_restricted', message || 'This feature is not available for your plan'),
  },

  resource: {
    userNotFound: (message?: string) => 
      createResourceError('user_not_found', message || 'Incorrect email'),
    
    leadNotFound: (message?: string) => 
      createResourceError('lead_not_found', message || 'Lead not found'),
    
    agentNotFound: (message?: string) => 
      createResourceError('agent_not_found', message || 'Agent not found'),
    
    conversationNotFound: (message?: string) => 
      createResourceError('conversation_not_found', message || 'Conversation not found'),
    
    ticketNotFound: (message?: string) => 
      createResourceError('ticket_not_found', message || 'Support ticket not found'),
  },

  conflict: {
    emailAlreadyRegistered: (message?: string) => 
      createConflictError('email_already_registered', message || 'This email is already registered'),
    
    duplicateLead: (message?: string) => 
      createConflictError('duplicate_lead', message || 'Lead already exists'),
    
    resourceInUse: (message?: string) => 
      createConflictError('resource_in_use', message || 'This resource is currently in use'),
    
    concurrentModification: (message?: string) => 
      createConflictError('concurrent_modification', message || 'This resource was modified by another user'),
  },

  business: {
    insufficientCredits: (message?: string) => 
      createBusinessError('insufficient_credits', message || 'You don\'t have enough credits'),
    
    subscriptionRequired: (message?: string) => 
      createBusinessError('subscription_required', message || 'A premium subscription is required'),
    
    paymentFailed: (message?: string) => 
      createBusinessError('payment_failed', message || 'Payment failed. Please try again'),
    
    quotaExceeded: (message?: string) => 
      createBusinessError('quota_exceeded', message || 'You\'ve reached your usage limit'),
  },

  rateLimit: {
    tooManyRequests: (message?: string) => 
      createRateLimitError('too_many_requests', message || 'Too many requests. Please wait a moment'),
    
    dailyQuotaReached: (message?: string) => 
      createRateLimitError('daily_quota_reached', message || 'Daily limit reached. Please try again tomorrow'),
    
    connectionLimit: (message?: string) => 
      createRateLimitError('connection_limit', message || 'Too many active connections'),
  },

  externalService: {
    stripeServiceDown: (message?: string) => 
      createExternalServiceError('stripe_service_down', message || 'Payment service is temporarily unavailable'),
    
    emailServiceFailed: (message?: string) => 
      createExternalServiceError('email_service_failed', message || 'Email delivery failed. Please try again'),
    
    s3UploadFailed: (message?: string) => 
      createExternalServiceError('s3_upload_failed', message || 'File upload failed. Please try again'),
    
    webhookDeliveryFailed: (message?: string) => 
      createExternalServiceError('webhook_delivery_failed', message || 'Webhook delivery failed'),
  },

  system: {
    databaseConnectionFailed: (message?: string) => 
      createSystemError('database_connection_failed', message || 'Database connection failed'),
    
    cacheServiceDown: (message?: string) => 
      createSystemError('cache_service_down', message || 'Cache service is unavailable'),
    
    internalProcessingError: (message?: string) => 
      createSystemError('internal_processing_error', message || 'An unexpected error occurred. Please try again'),
    
    serviceUnavailable: (message?: string) => 
      createSystemError('service_unavailable', message || 'Service is temporarily unavailable'),
  },
};

/**
 * Determines if an error should be retried
 */
export function shouldRetryError(error: AppError): boolean {
  // Retry system errors and external service errors
  if (isSystemError(error) || isExternalServiceError(error)) {
    return true;
  }
  
  // Retry rate limit errors after some time
  if (isRateLimitError(error)) {
    return true;
  }
  
  // Don't retry client errors (validation, authentication, authorization, resource, conflict, business)
  return false;
}

/**
 * Gets retry delay in milliseconds
 */
export function getRetryDelay(error: AppError, attempt: number): number {
  if (isRateLimitError(error)) {
    // Exponential backoff for rate limits: 1s, 2s, 4s, 8s, 16s, max 30s
    return Math.min(1000 * Math.pow(2, attempt - 1), 30000);
  }
  
  if (isSystemError(error) || isExternalServiceError(error)) {
    // Linear backoff for system errors: 1s, 2s, 3s, 4s, 5s
    return Math.min(1000 * attempt, 5000);
  }
  
  return 1000; // Default 1 second
}

/**
 * Determines if an error should be logged
 */
export function shouldLogError(error: AppError): boolean {
  // Always log system and external service errors
  if (isSystemError(error) || isExternalServiceError(error)) {
    return true;
  }
  
  // Log authorization errors for security monitoring
  if (isAuthorizationError(error)) {
    return true;
  }
  
  // Don't log client errors as they're expected
  return false;
}

/**
 * Gets error severity for monitoring
 */
export function getErrorSeverity(error: AppError): 'low' | 'medium' | 'high' | 'critical' {
  if (isSystemError(error)) {
    return 'critical';
  } else if (isExternalServiceError(error)) {
    return 'high';
  } else if (isAuthorizationError(error) || isRateLimitError(error) || isBusinessError(error)) {
    return 'medium';
  } else {
    // validation, authentication, resource, conflict errors
    return 'low';
  }
}
